import React, { useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowDown,
  faArrowUp,
  faMinus,
} from "@fortawesome/free-solid-svg-icons"
import type { TableModel } from "utils/tableModel"
import type { ScorecardTile as ScorecardTileDoc } from "features/Dashboard/types/dashboardDocument"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import {
  kpiBaseText,
  viewStatistic,
} from "features/Dashboard/runtime/tileView"
import { ordinal, scorecardValues } from "features/Dashboard/runtime/scorecard"
import { useTileTrace } from "./useTileTrace"
import TraceValue from "./TraceValue.component"

/**
 * A scorecard tile: what the row is, its value in the chosen column (the
 * latest by default), a chip with the change against the column before or
 * against a target, coloured by whether that is the good way, the column's
 * rank among the periods when asked, and the base. `model` is the tile's
 * view; `rawModel`, its last result, gives the base (see `kpiBaseText`).
 * A click on the value opens its trace.
 */
const ScorecardTile: React.FC<{
  tile: ScorecardTileDoc
  model: TableModel
  rawModel?: TableModel
}> = ({ tile, model, rawModel }) => {
  const controls = useDashboardStore((s) => s.tileControls[tile.id])
  // The statistic its view was built in, from its last result.
  const statistic = viewStatistic(tile, controls, rawModel ?? model)
  const { openTrace, panel: tracePanel } = useTileTrace(tile)
  const card = useMemo(
    () => scorecardValues(model, tile.visual, statistic),
    [model, tile.visual, statistic]
  )
  if (!card) return <p className="dq-tile__empty">No value for this pick.</p>
  const base = kpiBaseText(
    rawModel ?? model,
    model,
    { column: card.columnId, statistic },
    null
  )
  const tone = card.good === null ? "flat" : card.good ? "good" : "bad"
  // No good or bad: no change, or one that rounds to nothing.
  const icon =
    card.good === null || card.delta === null
      ? faMinus
      : card.delta > 0
        ? faArrowUp
        : faArrowDown
  return (
    <div className="dq-scorecard">
      <div className="dq-scorecard__head">
        <span className="dq-scorecard__label" title={card.label}>
          {card.label}
          {card.columnIsTotal ? "" : ` · ${card.columnLabel}`}
        </span>
        {card.rank && (
          <span className="dq-scorecard__rank">
            {ordinal(card.rank.position)} of {card.rank.of}
          </span>
        )}
      </div>
      <div className="dq-scorecard__value" aria-label="Value">
        <TraceValue
          text={card.text}
          onTrace={() =>
            openTrace({
              down: card.down,
              across: card.across,
              value: card.text,
            })
          }
        />
      </div>
      {card.deltaText && (
        <div className="dq-scorecard__change">
          <span className="dq-scorecard__chip" data-tone={tone}>
            <FontAwesomeIcon icon={icon} aria-hidden />
            <span>{card.deltaText}</span>
            {card.good !== null && (
              <span className="sr-only">
                {card.good ? ", better" : ", worse"}
              </span>
            )}
          </span>
          <span className="dq-scorecard__vs">vs {card.compareLabel}</span>
        </div>
      )}
      {base && <div className="dq-scorecard__base">Base {base}</div>}
      {tracePanel}
    </div>
  )
}

export default ScorecardTile
